import type { AnyObject, EmptyObject } from '../../../Types';
import { IController } from 'angular';

export type ViewLocals = AnyObject;

export type FunctionViewController = () => void;
export type ClassViewController<Locals extends ViewLocals = EmptyObject> = {
	new (...args: never[]): IController & Partial<Locals>;
};

export type ViewController<Locals extends ViewLocals = EmptyObject> = FunctionViewController | ClassViewController<Locals>;

class DefaultClassController {}

/**
 * @ngdoc type
 * @name mx.shell.View
 * @module mx.shell
 *
 * @description
 * Base view type to be used in {@ref mx.shell:mxSidePanel side panel}.<br>
 * It is not supposed to be used, use inherited types instead.
 *
 * @property {int | string} id - Unique identifier<br>Default value is <i>-1</i>
 * @property {string} name = 'Undefined Name' - Short name<br>Default value is <i>Undefined Name</i>
 * @property {string} displayName - Long name or title<br>Default value is undefined.
 * @property {string} controllerAs - A controller alias name. If present the controller will be
 * published to scope under the `controllerAs` name.
 * @property {object} locals - Designed for passing data into controller.<br>
 * All keys from `locals` object will be accessible in controller as property of its context `this`.<br>
 * Example: if `locals`: {myVar: 123} then in Controller: this.myVar === 123;
 * @property {boolean} bindToController - A <b>private read-only</b> property that determines whether controller is bound. Is automatically set to `true`
 * @property {string} type - Type of the connected object (e.g. 'incident', 'user', ...).<br>
 * If type is specified and registered then it's not necessary to specify `controller` and `templateUrl` parameters
 * they are obtained from {@ref mx.shell.SidePanelService#viewTypes SidePanel's viewTypes}.<br>
 * You may set `controller` and `templateUrl` values to redefine those params.<br>
 * If `type` is not set `controller` and `templateUrl` properties should be set.
 * @property {string | function} controller - Controller function that should be associated with a newly created
 * scope or the name of a registered controller if passed as a string.
 * @property {string} templateUrl - A path to html template.
 * @property {string} template - Html template.
 * @property {object} context - Object's context connected with this DetailsView.<br>
 * It will be passed into this DetailsView Controller via locals as Dependency Injections with name 'context'
 * If DetailsView contains `context` then this context will be added to `locals` automatically.<br>
 */
export default class View<Locals extends ViewLocals = ViewLocals> {
	public id: string | number = -1;
	public name = 'Undefined Name';

	public template = '';
	// public templateUrl?: string;

	public controller: ViewController<Locals> = DefaultClassController;
	public controllerAs = 'vm';
	public bindToController = true;

	public displayName = '';
	public type?: string;

	public locals: Locals = {} as Locals;

	constructor(options: Partial<View<Locals>> = {}) {
		Object.assign(this, options);
	}
}
